import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface PettyCashFilterDto {
  fromTransactionDate?: string;
  toTransactionDate?: string;
  entryType?: string;
  head?: string;
  transactionType?: string; // "Receipt", "Payment", or "All"
  pageIndex: number;
  pageSize: number;
}

export interface PettyCashLedgerDto {
  id: string;
  staffId: string;
  staffName: string;
  staffType?: string;
  entryType: string; // "Receipt", "Payment", "Adjustment"
  head: string; // "Agent Payment", "Voucher Collection", "Travel expenditure", "Company Transfer", "Manual"
  description?: string;
  receiptAmount: number;
  paymentAmount: number;
  openingBalance: number;
  closingBalance: number;
  transactionDate: string;
  referenceId?: string;
  referenceType?: string;
  isCancelled?: boolean;
  cancellationEntryId?: string;
  isAutomatic?: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface PettyCashPeriodSummaryDto {
  staffName?: string;
  openingBalance: number;
  closingBalance: number;
  totalReceipt: number;
  totalPayment: number;
  totalCount: number;
  netChange: number;
}

export interface PettyCashPaginatedResponse {
  items: PettyCashLedgerDto[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  summary?: PettyCashPeriodSummaryDto | null;
}

export interface RecordPettyCashReceiptDto {
  head: string; // "Agent Payment" | "Voucher Collection" | "Manual"
  receiptAmount: number;
  description?: string;
  transactionDate?: string;
  referenceId?: string;
  referenceType?: string;
}

export interface RecordPettyCashPaymentDto {
  head: string; // "Travel expenditure" | "Company Transfer" | "Manual"
  paymentAmount: number;
  description?: string;
  transactionDate?: string;
  referenceId?: string;
  referenceType?: string;
}

export interface CancelPettyCashDto {
  ledgerEntryId: string;
  reason?: string;
  transactionDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class PettyCashService {
  private readonly apiUrl = `${environment.apiConfig.uri}/api/PettyCash`;

  constructor(private http: HttpClient) {}

  getLedgerEntries(filter: PettyCashFilterDto): Observable<PettyCashPaginatedResponse> {
    let params = new HttpParams();

    const filterEntries: [string, string | number | undefined][] = [
      ['FromTransactionDate', filter.fromTransactionDate],
      ['ToTransactionDate', filter.toTransactionDate],
      ['EntryType', filter.entryType],
      ['Head', filter.head],
      ['TransactionType', filter.transactionType ?? 'All'],
      ['PageIndex', filter.pageIndex],
      ['PageSize', filter.pageSize],
    ];

    for (const [key, value] of filterEntries) {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    }

    return this.http.get<PettyCashPaginatedResponse>(this.apiUrl, { params });
  }

  getBalance(): Observable<number> {
    return this.http.get<number>(`${this.apiUrl}/balance`);
  }

  recordReceipt(dto: RecordPettyCashReceiptDto): Observable<PettyCashLedgerDto> {
    return this.http.post<PettyCashLedgerDto>(`${this.apiUrl}/receipt`, dto);
  }

  recordPayment(dto: RecordPettyCashPaymentDto): Observable<PettyCashLedgerDto> {
    return this.http.post<PettyCashLedgerDto>(`${this.apiUrl}/payment`, dto);
  }

  cancelLedgerEntry(dto: CancelPettyCashDto): Observable<PettyCashLedgerDto> {
    return this.http.post<PettyCashLedgerDto>(`${this.apiUrl}/cancel`, dto);
  }
}
